<template>
  <div v-shortkey="shortkeys" @shortkey="triggerShortkey">
    <transition name="fade">
      <div
        v-if="isReReporting"
        class="d-flex flex-column justify-content-center align-items-center"
      >
        <div class="general-information mx-auto">
          <h5 class="my-2">Accession Editing</h5>
        </div>
        <div class="col-6 border rounded">
          <select-input class="col" :items="editTypes" label="Edit Type" v-model="editType" />
          <text-area-input
            class="col"
            label="Edit Reason"
            :resize="false"
            rows="5"
            v-model="reasonForChange"
          />
        </div>
      </div>
    </transition>

    <div class="general-information border-bottom d-flex justify-content-start" ref="header">
      <h5 class="my-2">General Information</h5>
    </div>
    <div class="form-row row-wrap">
      <select-input
        v-model="specimen.protocolId"
        label="Protocol"
        id="protocol"
        accessKey="p"
        name="protocol"
        class="text-input col col-lg-3"
        ref="protocol"
        :required="isProtocolRequired"
        displayExpr="displayName"
        :items="protocolOptions"
        searchMode="startsWith"
      />
      <contact
        :multiple="true"
        label="Provider"
        class="col col-lg-6"
        ref="provider"
        id="provider"
        name="provider"
        accessKey="p"
        v-model="specimen.contact"
        :validator="$v.specimen.contact"
      />
      <ICDCodes
        ref="icd"
        :options-limit="50"
        v-model="specimen.icdCodes"
        :validator="$v.specimen.icdCodes"
        :multiple="true"
        class="text-input col col-lg-3 icd-select"
        trackBy="id"
        :internal-search="false"
        :hide-selected="true"
        placeholder="Search Options"
        :close-on-select="false"
        :shortkey="true"
        @remove="removeICD"
        :checkModified="true"
      />
      <number-input
        v-model="caseDetails.cytQtySlides"
        label="No. of Slides"
        id="numberOfSlides"
        :disabled="reScreen"
        class="col col-lg-2"
        name="cytQtySlides"
        ref="numberOfSlides"
        accessKey="n"
      />

      <transition name="fade">
        <TextInput
          disabled
          label="Screener"
          name="username"
          v-if="screener"
          class="col col-lg-3"
          id="username"
          :value="screener"
        />
      </transition>
      <transition name="fade">
        <TextInput
          disabled
          label="Reviewer"
          name="username"
          v-if="reviewer"
          class="col col-lg-3"
          id="username"
          :value="reviewer"
        />
      </transition>

      <transition name="fade">
        <select-input
          class="col"
          :items="reviewTypes"
          v-if="isScreening"
          accessKey="f"
          label="Forced Review"
          v-model="currentResult.cytReviewTypeRequestedId"
          id="forcedReview"
          ref="forcedReview"
          name="forcedReview"
        />
      </transition>
      <transition name="fade">
        <checkbox
          name="screenAgain"
          class="col col-lg-2 m-auto"
          id="screenAgain"
          v-model="currentResult.screenAgain"
          v-if="isReviewing && isPathologist"
          label="Screen Again"
        />
      </transition>
      <div
        class="col d-flex justify-content-end gap-x-4 align-items-start"
        :class="{
          'offset-3 col-lg-3': !lastScreening,
          'col-lg-6': lastScreening
        }"
      >
        <button v-if="!agreesWithPopup" @click="toggleAlternate" class="btn btn-primary">
          Alt. Screenings
        </button>
        <checkbox
          name="disagree"
          id="disagree"
          class="col col-lg-2"
          v-model="disagree"
          v-if="isReviewing || reScreen"
          label="Disagree"
        >
          <span class="h2">Disagree</span>
        </checkbox>
        <transition name="fade">
          <button
            @click="handleAddReview"
            :disabled="canAddReview"
            v-if="!isReviewing && !isCaseScreener && !isScreening"
            class="btn btn-primary"
          >
            Add <u>R</u>eview
          </button>
        </transition>
      </div>
    </div>
    <div class="builder-preview-container">
      <transition name="builder-preview" mode="out-in">
        <CytologyResultsBuilder
          v-if="renderBuilder"
          keep-alive
          key="builder"
          ref="resulter"
          v-stream:loaded="builderLoaded$"
          @submit="handleMacro"
        />
        <div
          class="form-row justify-content-between"
          v-else-if="renderPreview && previewText && previewText.length"
          key="preview"
        >
          <div class="col-12">
            <CytologyTextPreview :disabled="true" :selectedCodes="previewText" />
          </div>
        </div>
      </transition>
    </div>
    <div class="d-flex align-items-center justify-content-between mt-3">
      <div class="d-flex align-items-center justify-content-end ml-auto">
        <transition name="fade">
          <button
            class="btn btn-danger"
            v-if="lastScreening && isReviewing"
            @click="handleCancelReview"
          >
            Cancel
          </button>
        </transition>
        <transition name="fade">
          <button
            :disabled="formDisabled || isLoading"
            type="button"
            v-if="showDelayButton"
            @click="saveAndDelay"
            class="btn btn-warning ml-2"
          >
            {{ hasCytologyHold ? "Release" : "Save & Delay" }}
          </button>
        </transition>
        <button
          :disabled="formDisabled || isLoading"
          type="button"
          class="btn btn-primary ml-2 d-flex align-items-center save-btn"
          id="saveBtn"
          @click="saveResults"
        >
          <transition name="fade-fast" mode="out-in">
            <loader size="small" v-if="isLoading" key="loader"></loader>
            <span v-else key="text">Save</span>
          </transition>
        </button>
      </div>
    </div>
    <SystemMessage
      :canReview="isCaseReviewer"
      :isCytologist="isCytologist"
      @action="handleMesage"
      :isOpen="isSystemMessageOpen"
      @close="isSystemMessageOpen = false"
    />

    <modal :status="isAlternateOpen" @close="isAlternateOpen = false">
      <AlternateScreenings :specimenId="specimen.id"> </AlternateScreenings>
    </modal>
    <modal :status="isLocationPopupOpen" @close="isLocationPopupOpen = false">
      <form @submit.prevent="handleSelectLabLocation">
        <h2>Please select lab location:</h2>
        <select-input
          v-focus
          v-model="labLocationId"
          :items="availableLabLocations"
          :showClearButton="false"
        />
        <div class="d-flex justify-content-end">
          <loader v-if="isLoading" size="small" class="mr-1" />
          <button type="button" class="btn btn-danger mr-1" @click="handleCancel">Cancel</button>

          <button type="submit" class="btn btn-primary">Save</button>
        </div>
      </form>
    </modal>
    <modal :status="agreesWithPopup" @close="agreesWithPopup = false">
      <div class="p-4">
        <h4>Re-Screen Setup</h4>
        <p>Please indicate how you intend to proccess your re-screen.</p>
        <div class="gap-y-2 mt-4 d-flex flex-column">
          <select-input v-model="reviewAgree" label="Do you agree?" :items="yesNoOptions" />
          <select-input
            label="Result target type"
            name="resultTargetType"
            v-model="agreesWithCytResultTypeId"
            :items="agreeableResults"
          />
          <div class="d-flex justify-content-between mt-4">
            <button @click="toggleAlternate" class="btn btn-primary">Alt. Screenings</button>

            <button class="btn btn-primary ml-auto" @click="handleAgreePopupSubmit">Submit</button>
          </div>
        </div>
      </div>
    </modal>

    <modal :status="isAlternateOpen" @close="isAlternateOpen = false">
      <AlternateScreenings :specimenId="specimen.id" />
    </modal>
  </div>
</template>
<script>
import DropdownService from "@/services/dropdown";
import { helpers, required } from "vuelidate/lib/validators";
import ProviderAPI from "@/services/providers";
import { mapGetters, mapState } from "vuex";
import { Contact, ICDCodes } from "../Selectors";
import TextAreaInput from "../../TextAreaInput.vue";
import eventBus, {
  CYTOLOGY_SESSION_PROMPT,
  fromBusEvent,
  ICD_ENGINE_FINISH,
  ICD_ENGINE_START,
  LAST_SPECIMEN_SAVED,
  SAVE_FROM_CASE_HEADER
} from "../../../modules/eventBus";
import { cloneDeep, unset } from "lodash";
import SelectInput from "@/components/common/SelectInput.vue";
import Modal from "@/components/common/Modal.vue";
import Loader from "@/components/common/Loader.vue";
import {
  fixProviderPayload,
  getAltKeys,
  getEditTypes,
  sortCaseInsensitive,
  logShortKey
} from "@/modules/helpers";
import { initialEditors } from "@/store/ApplicationSettings";
import {
  CaseEditTypeEnum,
  CytCodeGroupEnum,
  CytResultTypeEnum,
  CytReviewTypeEnum,
  IcdOptionalStatusEnum
} from "@/modules/enums";
import NumberInput from "@/components/common/NumberInput.vue";
import TextInput from "@/components/common/TextInput.vue";
import cytologyEntityService, { CytologyEntities } from "@/services/cytology";
import CytologyTextPreview from "@/components/CytologyTextPreview.vue";
import CytologyResultsBuilder from "../CytologyResultsBuilder.vue";
import AlternateScreenings from "@/components/AlternateScreenings.vue";
import { switchMap, tap } from "rxjs/operators";
import cytologyResultsMixin from "@/mixins/cytologyResultsMixin";
import Checkbox from "@/components/common/Checkbox.vue";
import SystemMessage from "@/components/cytology/SystemMessage.vue";

export default {
  name: "Cytology-Results-Form",
  props: {
    value: {
      type: Object
    },
    isLoading: {
      type: Boolean
    },
    isUnlocked: Boolean
  },
  components: {
    CytologyResultsBuilder,
    SystemMessage,
    AlternateScreenings,
    Contact,
    Checkbox,
    ICDCodes,
    TextAreaInput,
    TextInput,
    SelectInput,
    CytologyTextPreview,
    Modal,
    Loader,
    NumberInput
  },
  mixins: [cytologyResultsMixin],
  data() {
    return {
      protocolOptions: [],
      isAlternateOpen: false,
      isLocationPopupOpen: false,
      cytResults: [],
      specimen: {},
      screenAgain: false,
      yesNoOptions: [
        {
          id: true,
          displayName: "Yes"
        },
        {
          id: false,
          displayName: "No"
        }
      ],
      reviewTypes: [
        {
          id: CytReviewTypeEnum.None,
          displayName: "None"
        },
        {
          id: CytReviewTypeEnum.Reviewer,
          displayName: "Reviewer"
        },
        {
          id: CytReviewTypeEnum.Pathologist,
          displayName: "Pathologist"
        }
      ],
      macroDialogType: 0,
      isBuilderOpen: false,
      disagree: false,
      isMacroDialogOpen: false,
      spellcheckRan: false,
      icdOptions: [],
      prefillCodes: [],
      icdSearch: DropdownService.searchIcdCodes,
      cptSearch: DropdownService.searchCptCodes,
      contactsSearch: ProviderAPI.searchContacts,
      pathologistSearch: DropdownService.searchPathologists,
      microscopic: "",
      caseStatuses: {},
      editTypes: getEditTypes(),
      cptDisplay: "",
      shortkeys: getAltKeys("peonfms")
    };
  },
  created() {
    if (this.casePrefix) {
      this.labPrefix = this.casePrefix;
      DropdownService.getProtocol(this.labPrefix.id).then(res => {
        this.protocolOptions = sortCaseInsensitive(res);
      });
    }
  },
  domStreams: ["$screenedSetup", "builderLoaded$", "builderQueue$"],
  subscriptions() {
    const $systemMessage = this.$screenedSetup.pipe(
      tap(() => {
        this.$nextTick(() => {
          this.setUserFocus();
        });
      })
    );
    const $cytologySession = fromBusEvent(CYTOLOGY_SESSION_PROMPT).pipe(
      switchMap(data => {
        return this.loadCytResults().then(() => data);
      }),
      tap(data => {
        this.handleLoadSession(data);
      })
    );
    const $specimenSave = fromBusEvent(LAST_SPECIMEN_SAVED).pipe(
      switchMap(data => {
        return this.loadCytResults().then(() => data);
      }),
      tap(data => {
        this.handleLoadSession(data);
      })
    );
    return {
      $systemMessage,
      $cytologySession,
      $specimenSave
    };
  },
  mounted() {
    if (this.labLocationId) {
      this.currentResult.resultedLabLocationId = this.labLocationId;
    }

    eventBus.$on(ICD_ENGINE_START, () => this.toggleIcd(true));
    eventBus.$on(ICD_ENGINE_FINISH, () => this.toggleIcd(false));
    eventBus.$on(SAVE_FROM_CASE_HEADER, () => this.triggerShortkey({ srcKey: "s" }));
  },
  beforeDestroy() {
    eventBus.$off(ICD_ENGINE_START);
    eventBus.$off(ICD_ENGINE_FINISH);
    eventBus.$off(SAVE_FROM_CASE_HEADER);
  },
  computed: {
    ...mapState({
      lastPathologist: state => state.sessionDetails.lastPathologist,
      textEditors: state => state.applicationSettings.textEditors || initialEditors,
      automaticMacroPopup: state =>
        state.applicationSettings.automaticMacroPopup !== null
          ? state.applicationSettings.automaticMacroPopup
          : state.labSettings.AutoMacroPopup,
      caseEditType: state => state.accessionStore.editType,
      caseDetails: state => state.accessionStore.caseDetails,
      casePrefix: state => state.accessionStore.casePrefix,
      caseHolds: state => state.accessionStore.caseHolds,
      specimens: state => state.accessionStore.specimens,
      SpellCheckOnSave: state => state.labSettings.SpellCheckOnSave,
      autoOpenEditors: state => state.applicationSettings.autoOpenEditors,
      confirmClearResultsMacro: state => state.applicationSettings.confirmClearResultsMacro,
      preserveMacroCaseNotes: state => state.applicationSettings.preserveMacroCaseNotes,
      ForceUpperCaseSite: state => state.labSettings.ForceUpperCaseSite,
      resultsMacroRetainScroll: state => state.applicationSettings.resultsMacroRetainScroll,
      autoOpenFirstEditorResults: state => state.applicationSettings.autoOpenFirstEditorResults,
      interfaceDiagnosisOptions: state => state.dropdowns.interfaceDiagnoses,
      availableLabLocations: state => state.availableLabLocations,
      sessionResultDate: state => state.sessionDetails.resultedOnDate,
      isSessionRescreen: state => state.sessionDetails.reScreen,
      currentLabLocation: state => state.currentLabLocation,
      cytologyPendingTestHoldCodeId: state => state.labSettings.CytologyPendingTestHoldCodeId,
      cytologyNoSignoutHoldCodeId: state => state.labSettings.CytologyNoSignoutHoldCodeId,
      hideCytologyDelay: state => state.applicationSettings.hideCytologyDelay
    }),
    ...mapGetters("accessionStore", ["isCaseEditable", "isReported", "primaryPathologist"]),
    ...mapGetters(["permissions", "cytReviewTypeId"]),
    labLocationId: {
      get() {
        return this.currentLabLocation;
      },
      set(value) {
        this.$store.dispatch("setCurrentLabLocation", value);
        return value;
      }
    },
    showDelayButton() {
      if (!this.isPathologist) {
        return false;
      }
      if (!this.cytologyNoSignoutHoldCodeId) {
        return false;
      }
      if (this.hasCytologyHold) {
        return true;
      }
      if (!this.hideCytologyDelay) {
        return false;
      }
      return true;
    },
    hasCytologyHold() {
      const holdCodes = this.caseHolds.filter(hold => {
        return hold.tagId === this.cytologyNoSignoutHoldCodeId;
      });

      return holdCodes.length > 0;
    },

    isProtocolRequired() {
      const currentPrefix = this.casePrefix;
      return currentPrefix && currentPrefix.isProtocolRequired;
    },
    userReviewTypeId() {
      return this.currentUser.cytReviewTypeId;
    },
    lastResult() {
      const lastScreenDate = this.lastScreening?.resultedOn;
      const lastReviewDate = this.lastReview?.resultedOn;
      if (this.lastReview) {
        if (lastScreenDate > lastReviewDate) {
          return this.lastScreening;
        }
        return this.lastReview;
      }
      return this.lastScreening;
    },
    reScreen: {
      get() {
        if (this.isSessionRescreen) {
          return true;
        }
        if (this.currentPreviewResult != null) {
          return this.currentPreviewResult?.cytResultTypeId === CytResultTypeEnum.Rescreening;
        }
        return this.isSessionRescreen;
      },
      set(val) {
        this.$store.commit("sessionDetails/setRescreening", val);
      }
    },
    currentScreener() {
      return this.screener === this.username || this.screener === this.useremail;
    },

    cytologyReviewTypeDisplay() {
      const values = Object.keys(CytReviewTypeEnum).reduce((acc, key) => {
        acc[CytReviewTypeEnum[key]] = key;
        return acc;
      }, {});
      const val = values[this.cytologyReviewType];

      return val;
    },

    isReReporting() {
      return [
        CaseEditTypeEnum.Amended,
        CaseEditTypeEnum.Addendum,
        CaseEditTypeEnum.Corrected,
        CaseEditTypeEnum.NonDiagnostic,
        CaseEditTypeEnum.Other,
        CaseEditTypeEnum.Billing
      ].includes(this.caseEditType);
    },

    editType: {
      get() {
        return this.caseEditType;
      },
      set(value) {
        this.$store.commit("accessionStore/setEditType", value);
        return value;
      }
    },

    reasonForChange: {
      get() {
        return this.$store.state.accessionStore.reasonForChange;
      },
      set(value) {
        this.$store.commit("accessionStore/setReasonForChange", value);
        return value;
      }
    },
    upperCaseSite() {
      if (Number(this.ForceUpperCaseSite)) {
        return true;
      }
      return false;
    }
  },
  validations() {
    return {
      specimen: {
        pathologists: {
          required
        },
        icdCodes: {
          required: vm => {
            return this.labSettings.CytologyIcdOptional === IcdOptionalStatusEnum.Off &&
              this.casePrefix.isBillingEnabled
              ? helpers.req(vm)
              : true;
          }
        }
      }
    };
  },
  watch: {
    disagree: {
      handler(val) {
        if ((this.isReviewing || this.reScreen) && !val) {
          this.handleAgree();
        } else {
          this.handleDisagree();
        }
      }
    },
    value: {
      immediate: true,
      handler(nv) {
        this.specimen = cloneDeep({
          ...nv,
          initIcdCodes: nv?.icdCodes ? nv.icdCodes : [],
          initCptCodes: nv?.cptCodes ? nv.cptCodes : [],
          initResultsMacros: nv?.resultsMacros ? nv.resultsMacros : []
        });
      }
    },
    "specimen.pathologists": {
      deep: true,
      handler(value) {
        const anyPrimary = value?.filter(e => e.isPrimary);
        if ((!anyPrimary?.length && value?.length) || anyPrimary?.length > 1) {
          this.setPrimaryPathologist(value[0].id);
        }
      }
    }
  },
  methods: {
    handleSelectLabLocation() {
      this.isLocationPopupOpen = false;
      this.currentResult.resultedLabLocationId = this.labLocationId;
      this.$store.dispatch("setCurrentLabLocation", this.labLocationId);
      this.saveResults();
    },
    async saveAndDelay() {
      try {
        if (this.isLoading) {
          return;
        }

        //Check prefix
        if (this.isProtocolRequired && !this.specimen.protocolId) {
          window.notify("Please select a protocol before saving.", "error");
          return;
        }
        const billableIcdCode = this.specimen.icdCodes?.length
          ? this.specimen.icdCodes.find(code => code.isBillable)
          : null;
        if (
          (this.labSettings.CytologyICDOptional == IcdOptionalStatusEnum.Warning &&
            this.specimen.icdCodes?.length === 0) ||
          !billableIcdCode
        ) {
          const confirm = await window.confirm(
            "You must have at least one billable icd code. <br/> Do you wish to continue? "
          );
          if (!confirm) {
            this.$store.commit("accessionStore/setLoading", false);
            return;
          }
        }
        // }
        const specimen = this.specimen;
        this.$store.dispatch("logTimeSinceBase", "save function triggered");
        //Validations
        // Must have atlead 1 adequacy & 1 Diagnosis
        const { diagCodes } = this.currentResult;
        if (diagCodes.length) {
          this.$store.commit("accessionStore/setLoading", false);
          let hasDiagnosis = false;
          let hasAdequacy = false;

          for (const cd of diagCodes) {
            const details = await cytologyEntityService.getSingleEntity(
              CytologyEntities.CytDiagnosticCodes,
              cd.cytDiagnosticCodeId
            );
            if (details?.cytCodeGroupId === CytCodeGroupEnum.Adequacy) {
              hasAdequacy = true;
            }
            if (details?.cytCodeGroupId === CytCodeGroupEnum.Diagnosis) {
              hasDiagnosis = true;
            }
          }
          if (!hasDiagnosis || !hasAdequacy) {
            this.$store.commit("accessionStore/setLoading", false);
            alert("Must have at least 1 adequacy and 1 diagnosis.");
            return;
          }
        }

        const newIcdCodes = specimen.icdCodes?.filter(
          e => specimen.initIcdCodes?.findIndex(val => val.id === e.id) === -1
        );
        const deletedIcdCodes = specimen.initIcdCodes?.filter(
          e => specimen.icdCodes?.findIndex(val => val.id === e.id) === -1
        );
        const updatedIcdCodes = specimen.initIcdCodes
          ?.map(val => {
            if (deletedIcdCodes?.findIndex(elem => elem.id === val.id) >= 0) {
              return { ...val, isDeleted: true };
            } else {
              return { ...val, isDeleted: false };
            }
          })
          .concat(newIcdCodes);

        unset(specimen, "initIcdCodes");
        unset(specimen, "initResultsMacros");
        specimen.contact = fixProviderPayload(specimen.contact);
        if (this.currentResult.resultedLabLocationId == null) {
          this.isLocationPopupOpen = true;
          return;
        }
        const cytResults = {
          ...this.currentResult,
          resultedBy: this.username,
          cytReviewTypeId: this.userReviewTypeId
        };
        return this.$emit("delay", {
          ...specimen,
          icdCodes: updatedIcdCodes,
          cytResults,
          cytQtySlides: this.caseDetails.cytQtySlides
        });
      } catch (error) {
        this.isLoading = false;
        window.notify("An error occurred while saving the case.", "error");
      } finally {
        this.isLoading = false;
      }
    },
    toggleAlternate() {
      this.isAlternateOpen = !this.isAlternateOpen;
    },

    handleChangePreview() {
      const isLastScreening =
        this.currentPreviewResult?.cytResultTypeId === this.lastScreening?.cytResultTypeId;
      if (isLastScreening) {
        this.currentPreviewResult = this.lastReview;
      } else {
        // Find next review
        const currentIdx = this.reviews.findIndex(e => e.id === this.currentPreviewResult.id);
        const nextIsEnd = currentIdx === this.reviews.length - 1;
        if (nextIsEnd) {
          this.currentPreviewResult = this.lastScreening;
        } else if (currentIdx === -1) {
          this.currentPreviewResult = this.lastReview;
        } else {
          const nextTarget = this.reviews[currentIdx + 1];
          const currentUserName = this.currentPreviewResult.resultedBy;
          if (currentUserName === this.username || currentUserName === this.useremail) {
            this.isReviewing = true;
            this.isScreening = false;
            this.isPreviewOpen = false;
            this.currentResult = nextTarget;
            this.currentPreviewResult = null;
          } else {
            this.isReviewing = false;
            this.currentPreviewResult = nextTarget;
            this.isPreviewOpen = true;
          }
        }
      }
    },

    handleMesage(response) {
      // 1-4
      switch (response) {
        case 1: {
          // Re-Screen
          this.enabled = true;
          this.currentResult.cytResultTypeId = CytResultTypeEnum.Rescreening;
          this.isScreening = true;
          this.isReviewing = false;
          this.isPreviewOpen = false;

          if (this.rescreenReviews && (this.lastReview || this.lastPathologistReview)) {
            this.agreesWithPopup = true;
          }
          break;
        }
        case 2: {
          // Review
          this.enabled = true;

          this.handleAddReview();
          break;
        }
        case 3: {
          this.enabled = false;
          this.isScreening = false;
          this.isReviewing = false;
          this.isPreviewOpen = true;
          break;
        }
        default: {
          this.enabled = false;
          break;
        }
      }
      this.isSystemMessageOpen = false;
      this.$screenedSetup.next();
    },

    async handleMacro(data) {
      const icdSet = new Set(data.diagnosisIcds);
      const icds = await Promise.all(Array.from(icdSet).map(e => this.icdSearch.byKey(e)));
      this.specimen.icdCodes = icds;
      this.currentResult = {
        ...this.currentResult,
        diagCodes: data.diagCodes,
        cytDiagnosticMacroId: data.cytDiagnosticMacroId,
        macroIsModified: data.macroIsModified
      };
    },
    triggerShortkey(event) {
      const srcKey = event.srcKey;
      if (srcKey === "s") {
        this.saveResults();
      }
      logShortKey(this.shortkeys[event.srcKey]);
      if (srcKey === "p") {
        //Check if protocol focused;
        const protocol = this.$refs["protocol"];
        if (protocol?.isFocused) {
          const provider = this.$refs["provider"];
          if (provider) {
            provider.focus();
          }
        } else {
          protocol.focus();
        }
      }

      if (srcKey === "e") {
        this.reScreenEl = this.$refs["reScreen"];
        if (this.reScreenEl) {
          this.reScreenEl.focus();
        }
      }
      if (srcKey === "f") {
        this.forcedReviewEl = this.$refs["forcedReview"];
        if (this.forcedReviewEl) {
          this.forcedReviewEl.focus();
        }
      }
      if (srcKey === "n") {
        const noSlides = this.$refs["numberOfSlides"];
        if (noSlides) {
          noSlides.focus();
        }
      }
      if (srcKey === "o") {
        const icdSelector = this.$refs["icd"];
        if (icdSelector) {
          this.$nextTick(icdSelector.toggleDropdown());
        }
      }
    },
    async saveResults(checkLoad = true) {
      if (this.isLoading && checkLoad) {
        return;
      }

      //Check prefix
      if (this.isProtocolRequired && !this.specimen.protocolId) {
        window.notify("Please select a protocol before saving.", "error");
        return;
      }
      const billableIcdCode = this.specimen.icdCodes?.length
        ? this.specimen.icdCodes.find(code => code.isBillable)
        : null;
      if (
        (this.labSettings.CytologyICDOptional == IcdOptionalStatusEnum.Warning &&
          this.specimen.icdCodes?.length === 0) ||
        !billableIcdCode
      ) {
        const confirm = await window.confirm(
          "You must have at least one billable icd code. <br/> Do you wish to continue? "
        );
        if (!confirm) {
          this.$store.commit("accessionStore/setLoading", false);
          return;
        }
      }
      // }
      const specimen = this.specimen;
      this.$store.dispatch("logTimeSinceBase", "save function triggered");
      //Validations
      // Must have atlead 1 adequacy & 1 Diagnosis
      const { diagCodes } = this.currentResult;
      if (diagCodes.length) {
        this.$store.commit("accessionStore/setLoading", false);
        let hasDiagnosis = false;
        let hasAdequacy = false;

        for (const cd of diagCodes) {
          const details = await cytologyEntityService.getSingleEntity(
            CytologyEntities.CytDiagnosticCodes,
            cd.cytDiagnosticCodeId
          );
          if (details?.cytCodeGroupId === CytCodeGroupEnum.Adequacy) {
            hasAdequacy = true;
          }
          if (details?.cytCodeGroupId === CytCodeGroupEnum.Diagnosis) {
            hasDiagnosis = true;
          }
        }
        if (!hasDiagnosis || !hasAdequacy) {
          this.$store.commit("accessionStore/setLoading", false);
          alert("Must have at least 1 adequacy and 1 diagnosis.");
          return;
        }
      }

      const newIcdCodes = specimen.icdCodes?.filter(
        e => specimen.initIcdCodes?.findIndex(val => val.id === e.id) === -1
      );
      const deletedIcdCodes = specimen.initIcdCodes?.filter(
        e => specimen.icdCodes?.findIndex(val => val.id === e.id) === -1
      );
      const updatedIcdCodes = specimen.initIcdCodes
        ?.map(val => {
          if (deletedIcdCodes?.findIndex(elem => elem.id === val.id) >= 0) {
            return { ...val, isDeleted: true };
          } else {
            return { ...val, isDeleted: false };
          }
        })
        .concat(newIcdCodes);

      unset(specimen, "initIcdCodes");
      unset(specimen, "initResultsMacros");
      specimen.contact = fixProviderPayload(specimen.contact);
      if (this.currentResult.resultedLabLocationId == null) {
        this.isLocationPopupOpen = true;
        return;
      }
      if (this.disagree) {
        this.currentResult.agreesWithPrevious = false;
      } else {
        this.currentResult.agreesWithPrevious = true;
      }
      const cytResults = {
        ...this.currentResult,
        resultedBy: this.username,
        cytReviewTypeId: this.userReviewTypeId
      };
      return this.$emit("save", {
        ...specimen,
        icdCodes: updatedIcdCodes,
        cytResults,
        cytQtySlides: this.caseDetails.cytQtySlides
      });
    },
    setPrimaryPathologist(id) {
      this.specimen.pathologists = this.specimen.pathologists.map(entry => {
        if (entry?.id === id) {
          entry.isPrimary = true;
        } else {
          entry.isPrimary = false;
        }
        return entry;
      });
    },
    removeICD(id) {
      this.specimen.icdCodes = this.specimen.icdCodes.filter(e => e.id !== id);
      this.specimen.isICDModified = true;
    }
  }
};
</script>

<style lang="scss" scoped>
.form-row {
  row-gap: 1rem;
  margin-bottom: 0.5rem;
}

.text-preview {
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5;
  color: #495057;
  margin-bottom: 0.5rem;
  width: 100%;
  margin: 0.5rem 0;
}

.gap-x-4 {
  column-gap: 1rem;
}
.gap-y-4 {
  row-gap: 1rem;
}
.tag-input {
  width: 100%;
  border: 1px solid #eee;
  font-size: 1rem;
  font-weight: 500;
  height: 50px;
  box-sizing: border-box;
  padding: 0 10px;
  border-radius: 0.25rem;
}

.result_macro {
  background-color: $primary;
}
.tag-input__tag {
  height: 30px;
  float: left;
  margin-right: 10px;
  background-color: $primary;
  margin-top: 10px;
  line-height: 30px;
  padding: 0 5px;
}

.tag-input__tag > span {
  cursor: pointer;
  opacity: 0.75;
}
.tag-btn {
  padding: 0px;
  margin: 0px;
}
.bg-gray {
  background-color: $gray;
}
@media screen and (min-width: 768px) {
  .confirm-dialog {
    width: 500px;
  }
}

.tag-input__tag {
  height: 30px;
  float: left;
  margin-right: 10px;
  background-color: $primary;
  margin-top: 10px;
  line-height: 30px;
  padding: 0 5px;
}

#editors {
  max-width: 100%;
}
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter, .slide-fade-leave-to
/* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateX(10px);
  opacity: 0;
}

/* Fade transitions for smooth appearance/disappearance */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-enter {
  opacity: 0;
  transform: translateY(-5px);
}
.fade-leave-to {
  opacity: 0;
  transform: translateY(5px);
}

/* Slower fade for preview text */
.fade-slow-enter-active,
.fade-slow-leave-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}
.fade-slow-enter {
  opacity: 0;
  transform: translateY(-10px);
}
.fade-slow-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Fast fade for button content switch */
.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: opacity 0.2s ease;
}
.fade-fast-enter,
.fade-fast-leave-to {
  opacity: 0;
}

/* Builder/Preview container */
.builder-preview-container {
  position: relative;
  overflow: hidden;
  min-height: 100px;
}

/* Builder/Preview exclusive transition */
.builder-preview-enter-active,
.builder-preview-leave-active {
  transition: opacity 0.3s ease;
}
.builder-preview-enter {
  opacity: 0;
}
.builder-preview-leave-to {
  opacity: 0;
}

/* Save button min-width to prevent layout shift */
.save-btn {
  min-width: 80px;
  justify-content: center;
}

/* Prevent layout shift for conditional fields */
.form-row {
  min-height: 80px;
}

/* Modal transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}
.modal-enter,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.3s ease;
}
.modal-enter .modal-content {
  transform: scale(0.9);
}
.modal-leave-to .modal-content {
  transform: scale(0.9);
}
</style>
